import { type ComponentProps, type ReactNode } from 'react';
import cn from 'clsx';
import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import { type OffsetFunction, type Align } from '@base-ui/react/internals/useAnchorPositioning';

import styles from './Popover.module.css';

type PopoverProps = {
  'aria-label': string;
  align?: Align;
  anchor?: ComponentProps<typeof PopoverPrimitive.Positioner>['anchor'];
  backdropClassName?: string;
  children: ReactNode;
  className?: string;
  drawer?: boolean;
  finalFocus?: ComponentProps<typeof PopoverPrimitive.Popup>['finalFocus'];
  initialFocus?: ComponentProps<typeof PopoverPrimitive.Popup>['initialFocus'];
  onBackdropClick?: ComponentProps<typeof PopoverPrimitive.Backdrop>['onClick'];
  portalContainer?: ComponentProps<typeof PopoverPrimitive.Portal>['container'];
  positionMethod?: ComponentProps<typeof PopoverPrimitive.Positioner>['positionMethod'];
  sideOffset?: number | OffsetFunction;
};

export const PopoverRoot = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverClose = PopoverPrimitive.Close;

export const Popover = ({
  align = 'start',
  anchor,
  backdropClassName,
  children,
  className,
  drawer = false,
  finalFocus,
  initialFocus,
  onBackdropClick,
  portalContainer,
  positionMethod,
  sideOffset = 2,
  'aria-label': ariaLabel,
}: PopoverProps) => (
  <PopoverPrimitive.Portal container={portalContainer}>
    <PopoverPrimitive.Backdrop
      className={cn(backdropClassName, drawer && styles.drawerBackdrop)}
      data-testid="search-components_popover-backdrop"
      onClick={onBackdropClick}
    />
    <PopoverPrimitive.Positioner
      anchor={anchor}
      side="bottom"
      align={align}
      positionMethod={positionMethod}
      sideOffset={sideOffset}
      collisionPadding={12}
      collisionAvoidance={{ side: 'flip', align: 'shift', fallbackAxisSide: 'none' }}
      className={cn(styles.positioner, drawer && styles.drawerPositioner)}
    >
      <PopoverPrimitive.Popup
        aria-label={ariaLabel}
        className={cn(styles.popover, drawer && styles.drawer, className)}
        finalFocus={finalFocus}
        initialFocus={initialFocus}
      >
        {children}
      </PopoverPrimitive.Popup>
    </PopoverPrimitive.Positioner>
  </PopoverPrimitive.Portal>
);
